<template>
  <div class="cashback-extrato">
    <Loading :isActive="(loading && !jaCarregou) || carregandoResumo" />

    <div class="cashback-extrato__topo">
      <CButton color="primary" variant="outline" class="cashback-botao-texto" @click="$emit('voltar')">
        <fa-icon icon="arrow-left" /> Voltar
      </CButton>
      <div>
        <div class="cashback-extrato__nome">{{ carteira.nome || `Cliente ${carteira.clienteId}` }}</div>
        <div class="cashback-extrato__detalhe">
          Código {{ carteira.clienteId }}
          <template v-if="carteira.cnpj"> · {{ formatDocumento(carteira.cnpj) }}</template>
          <template v-if="carteira.razaoSocial && carteira.razaoSocial !== carteira.nome"> · {{ carteira.razaoSocial }}</template>
          <template v-if="carteira.cidade"> · {{ carteira.cidade }}<template v-if="carteira.uf">/{{ carteira.uf }}</template></template>
        </div>
      </div>
      <div class="cashback-extrato__acoes">
        <CDropdown
          color="primary"
          placement="bottom-end"
          class="cashback-exportar"
          :disabled="exportandoExtrato"
        >
          <template #toggler-content>
            <fa-icon :icon="exportandoExtrato ? 'spinner' : 'file-alt'" :spin="exportandoExtrato" />
            {{ textoExportarExtrato }}
          </template>
          <CDropdownHeader>Extrato {{ descricaoPeriodoExtrato }}, conforme os filtros</CDropdownHeader>
          <div class="cashback-exportar__dica">{{ descricaoFiltrosExtrato }}</div>
          <CDropdownItem @click="baixarPdf()"><fa-icon icon="download" class="cashback-exportar__icone" /> Baixar PDF</CDropdownItem>
          <CDropdownItem :disabled="clienteBloqueado" :title="dicaEnvioExtrato" @click="enviarWhatsapp()">
            <fa-icon :icon="['fab', 'whatsapp']" class="cashback-exportar__icone" /> Enviar por WhatsApp
          </CDropdownItem>
          <CDropdownItem :disabled="clienteBloqueado" :title="dicaEnvioExtrato" @click="abrirEnvioEmail()">
            <fa-icon icon="envelope" class="cashback-exportar__icone" /> Enviar por e-mail
          </CDropdownItem>
          <div class="cashback-exportar__dica" :class="{ 'cashback-exportar__dica--bloqueio': clienteBloqueado }">{{ dicaEnvioExtrato }}</div>
          <CDropdownDivider />
          <CDropdownItem :disabled="revogandoLinks" @click="revogarLinks()">
            <fa-icon icon="ban" class="cashback-exportar__icone" /> Revogar links do extrato
          </CDropdownItem>
        </CDropdown>
        <CButton color="danger" variant="outline" class="cashback-botao-texto" :disabled="!resumoCarregado || carregandoResumo" @click="removendoSaldo = true">
          <fa-icon icon="minus-circle" /> Remover saldo
        </CButton>
        <CButton
          :color="resumo.bloqueio ? 'success' : 'danger'"
          variant="outline"
          class="cashback-botao-texto"
          :disabled="!resumoCarregado || carregandoResumo || alterandoBloqueio"
          @click="resumo.bloqueio ? desbloquear() : bloquear()"
        >
          <fa-icon :icon="resumo.bloqueio ? 'unlock' : 'ban'" /> {{ resumo.bloqueio ? "Desbloquear" : "Bloquear no programa" }}
        </CButton>
      </div>
    </div>

    <div v-if="resumo.bloqueio" class="cashback-extrato__bloqueio" role="status">
      <fa-icon icon="ban" class="mr-2" />
      <div>
        <strong>Cliente bloqueado no programa de cashback.</strong> Não ganha nem usa cashback e não vê nada do programa nos canais de venda; o saldo fica guardado com o vencimento normal.
        <div class="cashback-extrato__bloqueio-motivo">
          {{ resumo.bloqueio.motivo }}
          <span v-if="resumo.bloqueio.colaborador || resumo.bloqueio.bloqueadoEm">
            · {{ [resumo.bloqueio.colaborador, resumo.bloqueio.bloqueadoEm ? formatData(resumo.bloqueio.bloqueadoEm) : null].filter(Boolean).join(" em ") }}
          </span>
        </div>
      </div>
    </div>

    <div class="cashback-extrato__cards">
      <div class="cashback-extrato__card">
        <div class="cashback-extrato__rotulo">Saldo atual</div>
        <div :class="['cashback-extrato__valor', classeSaldo(resumo.saldo)]">{{ formatMoney(resumo.saldo) }}</div>
      </div>
      <div class="cashback-extrato__card">
        <div class="cashback-extrato__rotulo">Próximo vencimento</div>
        <div class="cashback-extrato__valor cashback-extrato__valor--atencao">
          <template v-if="resumo.proximoVencimento">
            {{ formatMoney(resumo.proximoVencimento.valor) }}
            <small>em {{ formatData(resumo.proximoVencimento.data) }}</small>
          </template>
          <template v-else>—</template>
        </div>
      </div>
      <div class="cashback-pendente-card">
        <div class="cashback-pendente-card__rotulo">Cashback pendente</div>
        <div :class="['cashback-pendente-card__valor', { 'cashback-pendente-card__valor--vazio': !resumo.totalPendente }]">
          <template v-if="resumo.totalPendente">
            {{ formatMoney(resumo.totalPendente) }}
            <small>{{ resumo.pendentes.length === 1 ? "1 pedido" : `${resumo.pendentes.length} pedidos` }} aguardando faturamento</small>
          </template>
          <template v-else>—</template>
        </div>
      </div>
      <div class="cashback-extrato__card">
        <div class="cashback-extrato__rotulo">Movimentos</div>
        <div class="cashback-extrato__valor">{{ totalMovimentos }}</div>
      </div>
      <div v-if="resumo.mediaAtrasoIndisponivel" class="cashback-extrato__card">
        <div class="cashback-extrato__rotulo">Média de atraso</div>
        <div class="cashback-extrato__valor">
          Indisponível
          <small>não foi possível consultar os títulos pagos no ERP</small>
        </div>
      </div>
      <div v-for="atraso in resumo.mediaAtrasoIndisponivel ? [] : resumo.mediaAtraso" :key="`atraso-${atraso.meses}`" class="cashback-extrato__card">
        <div class="cashback-extrato__rotulo">Média de atraso · {{ atraso.meses }} {{ atraso.meses === 1 ? "mês" : "meses" }}</div>
        <div class="cashback-extrato__valor">
          <template v-if="atraso.media !== null">
            {{ formatDias(atraso.media) }}
            <small>maior atraso {{ formatDias(atraso.maior) }} · {{ atraso.titulos }} {{ atraso.titulos === 1 ? "título pago" : "títulos pagos" }}</small>
          </template>
          <template v-else>
            —
            <small>sem título pago no período</small>
          </template>
        </div>
      </div>
    </div>

    <h6 class="cashback-subtitulo">A vencer</h6>
    <div class="cashback-tabela-caixa">
      <table class="cashback-tabela">
        <thead>
          <tr>
            <th style="width: 160px">Vence em</th>
            <th>Origem</th>
            <th style="width: 160px">Valor a vencer</th>
            <th style="width: 40px"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!vencimentos.length">
            <td colspan="4" class="cashback-tabela__vazio">Nenhum valor a vencer</td>
          </tr>
          <template v-for="vencimento in vencimentos">
            <tr
              :key="`data-${vencimento.data}`"
              class="cashback-vencimento__linha"
              :tabindex="vencimento.lotes.length > 1 ? 0 : null"
              :aria-expanded="vencimento.lotes.length > 1 ? String(!!vencimentosAbertos[vencimento.data]) : null"
              @click="alternarVencimento(vencimento.data)"
              @keydown.enter="alternarVencimento(vencimento.data)"
            >
              <td class="cashback-tabela__destaque">
                <fa-icon icon="clock" class="cashback-vencimento__icone" />
                {{ formatData(vencimento.data) }}
                <span v-if="diasPara(vencimento.data) !== null" class="cashback-vencimento__prazo">{{ textoPrazo(vencimento.data) }}</span>
              </td>
              <td class="cashback-tabela__secundario">
                {{ vencimento.lotes.length === 1 ? `Pedido ${vencimento.lotes[0].pedidoErpId || "—"}` : `${vencimento.lotes.length} ganhos` }}
              </td>
              <td class="cashback-vencimento__valor">{{ formatMoney(vencimento.valor) }}</td>
              <td class="cashback-vencimento__seta">
                <fa-icon v-if="vencimento.lotes.length > 1" :icon="vencimentosAbertos[vencimento.data] ? 'chevron-up' : 'chevron-down'" />
              </td>
            </tr>
            <template v-if="vencimento.lotes.length > 1 && vencimentosAbertos[vencimento.data]">
              <tr v-for="lote in vencimento.lotes" :key="`lote-${lote.id}`" class="cashback-vencimento__detalhe">
                <td></td>
                <td>Pedido <strong>{{ lote.pedidoErpId || "—" }}</strong> · ganho de {{ formatMoney(lote.valorOriginal) }}</td>
                <td>{{ formatMoney(lote.saldo) }}</td>
                <td></td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
    </div>

    <h6 class="cashback-subtitulo">Movimentos</h6>
    <div class="cashback-filtros">
      <div class="cashback-extrato__filtros">
        <div class="cashback-busca">
          <input
            class="form-control"
            v-model="filtros.q"
            placeholder="Pedido ERP ou orçamento..."
            aria-label="Buscar por pedido ERP ou orçamento"
            @input="loadComDebounce()"
          />
          <fa-icon v-if="atualizando" icon="spinner" spin class="cashback-busca__indicador" />
        </div>
        <select class="form-control" v-model="filtros.tipos" aria-label="Tipo de movimento" @change="load()">
          <option value="">Todos os tipos</option>
          <option :value="filtroPendenteValor">Pendentes de faturamento</option>
          <option v-for="(rotulo, tipo) in tiposMovimento" :key="tipo" :value="tipo">{{ rotulo }}</option>
        </select>
        <input class="form-control" type="date" v-model="filtros.dtIni" title="De" aria-label="Movimentos de" @change="load()" />
        <input class="form-control" type="date" v-model="filtros.dtFim" title="Até" aria-label="Movimentos até" @change="load()" />
        <CButton
          color="primary"
          class="cashback-botao-icone"
          :disabled="exportando || filtroPendente"
          :title="tituloExportacao"
          :aria-label="tituloExportacao"
          @click="baixarExcel()"
        >
          <fa-icon :icon="exportando ? 'spinner' : 'download'" :spin="exportando" />
        </CButton>
      </div>

      <div class="cashback-chips" v-if="chipsFiltros.length">
        <span class="cashback-chips__titulo">Filtrando por</span>
        <span class="cashback-chip" v-for="chip in chipsFiltros" :key="chip.chave">
          <strong>{{ chip.rotulo }}:</strong> {{ chip.valor }}
          <button
            type="button"
            class="cashback-chip__remover"
            title="Remover filtro"
            :aria-label="`Remover filtro ${chip.rotulo}`"
            @click="removerFiltro(chip.chave)"
          >
            &times;
          </button>
        </span>
        <button type="button" class="cashback-chips__limpar" @click="limparFiltros()">limpar tudo</button>
      </div>
    </div>

    <div :class="['cashback-tabela-caixa', { 'cashback-tabela-caixa--atualizando': atualizando }]">
      <table class="cashback-tabela">
        <thead>
          <tr>
            <th style="width: 145px">Quando</th>
            <th style="width: 180px">Tipo</th>
            <th>Regra</th>
            <th style="width: 130px">Orçamento</th>
            <th style="width: 120px">Pedido ERP</th>
            <th style="width: 110px">Valor</th>
            <th style="width: 110px">Saldo após</th>
            <th style="width: 100px">Validade</th>
            <th>Motivo</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="erroCarga">
            <td colspan="9" class="cashback-tabela__vazio">
              <div class="cashback-tabela__erro">Não foi possível carregar os movimentos: {{ erroCarga }}</div>
              <CButton color="primary" variant="outline" size="sm" @click="load(paginaAtual)">Tentar novamente</CButton>
            </td>
          </tr>
          <tr v-else-if="!linhasPendentes.length && !movimentos.length">
            <td colspan="9" class="cashback-tabela__vazio">Nenhum movimento para os filtros</td>
          </tr>
          <tr v-for="pendente in linhasPendentes" :key="`pendente-${pendente.pedidoErpId}`" class="cashback-pendente__linha">
            <td>{{ pendente.data | datetime }}</td>
            <td>
              <span class="cashback-etiqueta cashback-pendente__etiqueta"><fa-icon icon="clock" class="mr-1" />Pendente</span>
            </td>
            <td>{{ (pendente.regras || []).filter(Boolean).join(", ") || "—" }}</td>
            <td class="cashback-tabela__codigo">{{ pendente.orcamentoId || "—" }}</td>
            <td class="cashback-tabela__codigo">{{ pendente.pedidoErpId || "—" }}</td>
            <td class="cashback-pendente__valor">
              +{{ formatMoney(pendente.valor) }}
              <small class="cashback-pendente__previsto">previsto</small>
            </td>
            <td>—</td>
            <td>—</td>
            <td class="cashback-tabela__secundario">{{ pendente.mensagem || "O cashback entra na carteira quando o pedido for faturado." }}</td>
          </tr>
          <tr v-for="movimento in movimentos" :key="movimento.id">
            <td>{{ movimento.data | datetime }}</td>
            <td><span :class="['cashback-etiqueta', etiquetas[movimento.tipo]]">{{ movimento.tipoLabel }}</span></td>
            <td>{{ movimento.regra || "—" }}</td>
            <td class="cashback-tabela__codigo">{{ movimento.orcamentoId || "—" }}</td>
            <td class="cashback-tabela__codigo">{{ movimento.pedidoErpId || "—" }}</td>
            <td :class="movimento.valor < 0 ? 'cashback-valor--negativo' : 'cashback-valor--positivo'">{{ formatMoney(movimento.valor) }}</td>
            <td>{{ formatMoney(movimento.saldoDepois) }}</td>
            <td>{{ formatData(movimento.dataValidade) }}</td>
            <td class="cashback-tabela__secundario">{{ movimento.motivo }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="cashback-paginacao" v-if="!filtroPendente && paginator.last_page > 1">
      <pagination :data="paginator" :limit="5" @pagination-change-page="load" />
    </div>

    <RemoverSaldoModal :show.sync="removendoSaldo" :carteira="carteira" :saldo="Number(resumo.saldo || 0)" @removido="aposAlterar" />
    <BloquearClienteModal :show.sync="bloqueando" :cliente="clienteBloqueio" @bloqueado="aposAlterar" />
    <EnviarExtratoEmailModal :show.sync="enviandoEmail" :carteira="carteira" :periodo="filtrosExtrato" />
  </div>
</template>

<script>
import _debounce from "lodash/debounce";
import Loading from "../../../components/Loading";
import listagemMixin from "@/mixins/listagemMixin";
import { del, get, post } from "@/helpers/apiRequest";
import RemoverSaldoModal from "./RemoverSaldoModal.vue";
import BloquearClienteModal from "./BloquearClienteModal.vue";
import EnviarExtratoEmailModal from "./EnviarExtratoEmailModal.vue";
import { copiarTexto } from "./copiarTexto";
import { downloadBlob } from "@/helpers/downloadBlob";
import {
  DEBOUNCE_BUSCA_MS,
  DIAS_VALIDADE_LINK_EXTRATO,
  DURACAO_AVISO_SUCESSO_MS,
  ETIQUETA_MOVIMENTO,
  FILTRO_PENDENTE,
  LIMITE_EXPORTACAO,
  MS_POR_DIA,
  TIPOS_MOVIMENTO,
  classeSaldo,
  descricaoPeriodoExtrato,
  formatData,
  formatDocumento,
  formatMoney,
  mensagemErroApi,
} from "../helpers";

export default {
  name: "ExtratoCarteira",
  components: { Loading, RemoverSaldoModal, BloquearClienteModal, EnviarExtratoEmailModal },
  mixins: [listagemMixin],

  props: {
    carteira: { type: Object, required: true },
  },

  data() {
    return {
      listagemEndpoint: "/admin/cashback/movimentos",
      carregandoResumo: false,
      resumoCarregado: false,
      removendoSaldo: false,
      alterandoBloqueio: false,
      bloqueando: false,
      exportando: false,
      gerandoPdf: false,
      gerandoLink: false,
      revogandoLinks: false,
      enviandoEmail: false,
      jaCarregou: false,
      erroCargaNaTela: true,
      resumo: {
        saldo: this.carteira.saldo,
        proximoVencimento: this.carteira.proximoVencimento,
        lotesAVencer: [],
        pendentes: [],
        totalPendente: 0,
        bloqueio: null,
        mediaAtraso: [],
        mediaAtrasoIndisponivel: false,
      },
      filtros: { q: "", tipos: "", dtIni: "", dtFim: "" },
      vencimentosAbertos: {},
      rotulosFiltros: { q: "Pedido/orçamento", tipos: "Tipo", dtIni: "De", dtFim: "Até" },
      tiposMovimento: TIPOS_MOVIMENTO,
      etiquetas: ETIQUETA_MOVIMENTO,
      filtroPendenteValor: FILTRO_PENDENTE,
    };
  },

  computed: {
    clienteBloqueio() {
      return { clienteId: this.carteira.clienteId, nome: this.carteira.nome, saldo: Number(this.resumo.saldo || 0) };
    },

    periodoExtrato() {
      const periodo = {};

      if (this.filtros.dtIni) periodo.dtIni = this.filtros.dtIni;
      if (this.filtros.dtFim) periodo.dtFim = this.filtros.dtFim;

      return periodo;
    },

    filtrosExtrato() {
      const filtros = { ...this.periodoExtrato };
      const { tipos } = this.filtros;

      if (tipos && tipos !== FILTRO_PENDENTE) filtros.tipos = [tipos];

      return filtros;
    },

    descricaoPeriodoExtrato() {
      return descricaoPeriodoExtrato(this.periodoExtrato);
    },

    descricaoFiltrosExtrato() {
      const { tipos } = this.filtrosExtrato;

      if (!tipos) return "Todos os tipos de movimento.";

      return `Só ${tipos.map((tipo) => this.tiposMovimento[tipo] || tipo).join(", ")}.`;
    },

    clienteBloqueado() {
      return !!this.resumo.bloqueio;
    },

    dicaEnvioExtrato() {
      if (this.clienteBloqueado) return "Cliente bloqueado: o link não abre o extrato e o e-mail não é enviado. Desbloqueie para enviar.";

      return `O link vale ${DIAS_VALIDADE_LINK_EXTRATO} dias e quem tiver o link vê o extrato do cliente.`;
    },

    exportandoExtrato() {
      return this.gerandoPdf || this.gerandoLink;
    },

    textoExportarExtrato() {
      if (this.gerandoPdf) return "Gerando PDF...";
      if (this.gerandoLink) return "Gerando link...";

      return "Exportar extrato";
    },

    filtroPendente() {
      return this.filtros.tipos === FILTRO_PENDENTE;
    },

    movimentos() {
      return this.filtroPendente ? [] : this.items;
    },

    paginaAtual() {
      return this.paginator.current_page || 1;
    },

    totalMovimentos() {
      if (!this.filtroPendente && this.paginator && this.paginator.total !== undefined) return this.paginator.total;

      return this.carteira.quantidadeMovimentos;
    },

    tituloExportacao() {
      if (this.exportando) return "Gerando a planilha...";
      if (this.filtroPendente) return "Os pendentes de faturamento não entram na planilha. Troque o filtro de tipo para exportar.";

      return "Baixar Excel dos movimentos filtrados";
    },

    linhasPendentes() {
      const { q, tipos, dtIni, dtFim } = this.filtros;
      const primeiraPagina = !this.paginator || !this.paginator.current_page || this.paginator.current_page === 1;

      if ((tipos && tipos !== FILTRO_PENDENTE) || !primeiraPagina) return [];

      const termo = String(q || "").trim().toLowerCase();

      return this.resumo.pendentes.filter((pendente) => {
        const dia = String(pendente.data || "").substring(0, 10);

        if (termo && ![pendente.pedidoErpId, pendente.orcamentoId].some((valor) => String(valor || "").toLowerCase() === termo)) {
          return false;
        }

        if (dtIni && dia < dtIni) return false;

        return !(dtFim && dia > dtFim);
      });
    },

    vencimentos() {
      const grupos = this.resumo.lotesAVencer.reduce((acumulado, lote) => {
        const data = String(lote.dataValidade || "").substring(0, 10);
        const grupo = acumulado[data] || { data, valor: 0, lotes: [] };

        grupo.valor += Number(lote.saldo || 0);
        grupo.lotes.push(lote);
        acumulado[data] = grupo;

        return acumulado;
      }, {});

      return Object.values(grupos).sort((a, b) => a.data.localeCompare(b.data));
    },

    atualizando() {
      return this.loading && this.jaCarregou;
    },
  },

  watch: {
    loading(carregando) {
      if (!carregando) this.jaCarregou = true;
    },
  },

  mounted() {
    this.carregarResumo();
  },

  methods: {
    carregarResumo() {
      this.carregandoResumo = true;

      get(`/admin/cashback/clientes/${encodeURIComponent(this.carteira.clienteId)}/extrato?take=1`)
        .then((json) => {
          this.resumo = {
            saldo: json.saldo,
            proximoVencimento: json.proximoVencimento,
            lotesAVencer: json.lotesAVencer || [],
            pendentes: json.pendentes || [],
            totalPendente: Number(json.totalPendente || 0),
            bloqueio: json.bloqueio || null,
            mediaAtraso: json.mediaAtraso || [],
            mediaAtrasoIndisponivel: !!json.mediaAtrasoIndisponivel,
          };
          this.resumoCarregado = true;
        })
        .catch((err) => this.$swal({ title: "Erro ao carregar o extrato", text: mensagemErroApi(err), icon: "error" }))
        .finally(() => {
          this.carregandoResumo = false;
        });
    },

    aposAlterar() {
      this.$emit("alterado");
      this.carregarResumo();
      this.load(1);
    },

    bloquear() {
      this.bloqueando = true;
    },

    desbloquear() {
      return this.$swal({
        title: "Desbloquear o cliente?",
        text: "Os próximos pedidos voltam a gerar cashback conforme as regras e o saldo que ainda não venceu volta a poder ser usado. Pedidos feitos durante o bloqueio não são recalculados.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Desbloquear",
        cancelButtonText: "Cancelar",
      }).then((res) => {
        if (!res.value) return;

        this.alterandoBloqueio = true;
        return del(`/admin/cashback/bloqueios/${encodeURIComponent(this.carteira.clienteId)}`)
          .then(() => this.aposAlterar())
          .catch((err) => this.$swal({ title: "Não foi possível desbloquear", text: mensagemErroApi(err), icon: "error" }))
          .finally(() => {
            this.alterandoBloqueio = false;
          });
      });
    },

    formatDias(dias) {
      if (dias === null || dias === undefined) return "—";

      const valor = Number(dias).toLocaleString("pt-BR", { maximumFractionDigits: 1 });

      return `${valor} ${Number(dias) === 1 ? "dia" : "dias"}`;
    },

    load(page = 1) {
      if (!this.filtroPendente) return listagemMixin.methods.load.call(this, page);

      this._loadSeq = (this._loadSeq || 0) + 1;
      this.items = [];
      this.paginator = {};
      this.erroCarga = null;
      this.loading = false;

      return Promise.resolve();
    },

    appendFiltros(params) {
      params.append("cliente_id", this.carteira.clienteId);

      Object.entries(this.filtros).forEach(([chave, valor]) => {
        if (valor === "" || valor === null) return;

        if (chave === "tipos" && valor === FILTRO_PENDENTE) return;

        params.append(chave === "tipos" ? "tipos[]" : chave, valor);
      });
    },

    descreverFiltro(chave, valor) {
      if (chave === "tipos") return valor === FILTRO_PENDENTE ? "Pendentes de faturamento" : this.tiposMovimento[valor] || valor;
      if (chave === "dtIni" || chave === "dtFim") return formatData(valor);

      return valor;
    },

    loadComDebounce: _debounce(function () {
      this.load();
    }, DEBOUNCE_BUSCA_MS),

    async confirmarExportacaoTruncada() {
      const total = Number(this.paginator && this.paginator.total) || 0;

      if (total <= LIMITE_EXPORTACAO) return true;

      const resposta = await this.$swal({
        title: "A planilha vai sair incompleta",
        text: `Os filtros trazem ${total.toLocaleString("pt-BR")} movimentos, mas a planilha leva só os primeiros ${LIMITE_EXPORTACAO.toLocaleString("pt-BR")}. Refine os filtros para exportar tudo.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Baixar mesmo assim",
        cancelButtonText: "Cancelar",
      });

      return !!(resposta && resposta.value);
    },

    async baixarExcel() {
      if (this.filtroPendente || !(await this.confirmarExportacaoTruncada())) return;

      this.exportando = true;

      try {
        const params = new URLSearchParams();
        this.appendFiltros(params);

        await downloadBlob(`/admin/cashback/movimentos/exportar?${params}`, "movimentos-cashback.xlsx");
      } catch (error) {
        this.$swal({ title: "Erro ao exportar", text: mensagemErroApi(error), icon: "error" });
      } finally {
        this.exportando = false;
      }
    },

    urlExtrato(acao) {
      return `/admin/cashback/clientes/${encodeURIComponent(this.carteira.clienteId)}/extrato/${acao}`;
    },

    async baixarPdf() {
      if (this.exportandoExtrato) return;

      this.gerandoPdf = true;

      try {
        const params = this.parametrosExtrato();

        await downloadBlob(`${this.urlExtrato("pdf")}${params ? `?${params}` : ""}`, `extrato-cashback-${this.carteira.clienteId}.pdf`);
      } catch (error) {
        this.$swal({ title: "Erro ao gerar o PDF", text: mensagemErroApi(error), icon: "error" });
      } finally {
        this.gerandoPdf = false;
      }
    },

    parametrosExtrato() {
      const params = new URLSearchParams();

      Object.entries(this.filtrosExtrato).forEach(([chave, valor]) => {
        if (Array.isArray(valor)) {
          valor.forEach((item) => params.append(`${chave}[]`, item));

          return;
        }

        params.append(chave, valor);
      });

      return params.toString();
    },

    abrirEnvioEmail() {
      if (!this.clienteBloqueado) this.enviandoEmail = true;
    },

    async enviarWhatsapp() {
      if (this.exportandoExtrato || this.clienteBloqueado) return;

      this.gerandoLink = true;
      let resposta;

      try {
        resposta = await post(this.urlExtrato("link"), this.filtrosExtrato);
      } catch (error) {
        this.$swal({ title: "Erro ao gerar o link do extrato", text: mensagemErroApi(error), icon: "error" });

        return;
      } finally {
        this.gerandoLink = false;
      }

      const mensagem = (resposta && (resposta.mensagem || resposta.link)) || "";

      if (!mensagem) {
        this.$swal({ title: "Erro ao gerar o link do extrato", text: "A API não devolveu o link do extrato.", icon: "error" });

        return;
      }

      const escolha = await this.$swal({
        title: "Extrato pronto para enviar",
        text: mensagem,
        footer: [
          resposta.expiraEm ? `O link vale até ${formatData(resposta.expiraEm)}.` : `O link vale ${DIAS_VALIDADE_LINK_EXTRATO} dias.`,
          "Quem tiver o link vê o extrato do cliente. Se for enviado à pessoa errada, use Revogar links do extrato.",
        ].join(" "),
        icon: "success",
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: "Abrir no WhatsApp",
        denyButtonText: "Copiar link",
        cancelButtonText: "Fechar",
      });

      if (escolha && escolha.isConfirmed) {
        window.open(`https://wa.me/?text=${encodeURIComponent(mensagem)}`, "_blank", "noopener");

        return;
      }

      if (escolha && escolha.isDenied) await this.copiarLink(resposta.link);
    },

    async revogarLinks() {
      if (this.revogandoLinks) return;

      const confirmacao = await this.$swal({
        title: "Revogar os links do extrato?",
        text: "Todos os links do extrato já enviados para este cliente deixam de abrir. Para enviar o extrato de novo, gere um link novo.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Revogar links",
        cancelButtonText: "Cancelar",
      });

      if (!confirmacao || !confirmacao.value) return;

      this.revogandoLinks = true;

      try {
        const json = await post(this.urlExtrato("revogar-links"), {});

        this.$swal({
          title: "Links revogados",
          text: (json && json.message) || "Os links do extrato enviados para este cliente não abrem mais.",
          icon: "success",
          timer: DURACAO_AVISO_SUCESSO_MS,
          showConfirmButton: false,
        });
      } catch (error) {
        this.$swal({ title: "Não foi possível revogar os links", text: mensagemErroApi(error), icon: "error" });
      } finally {
        this.revogandoLinks = false;
      }
    },

    async copiarLink(link) {
      try {
        await copiarTexto(link);
        this.$swal({ title: "Link copiado", text: "Cole o link na conversa com o cliente.", icon: "success", timer: DURACAO_AVISO_SUCESSO_MS, showConfirmButton: false });
      } catch (error) {
        this.$swal({ title: "Não foi possível copiar o link", text: link, icon: "warning" });
      }
    },

    alternarVencimento(data) {
      this.$set(this.vencimentosAbertos, data, !this.vencimentosAbertos[data]);
    },

    diasPara(data) {
      if (!data) return null;

      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      const alvo = new Date(`${String(data).substring(0, 10)}T00:00:00`);

      return Number.isNaN(alvo.getTime()) ? null : Math.round((alvo - hoje) / MS_POR_DIA);
    },

    textoPrazo(data) {
      const dias = this.diasPara(data);

      if (dias <= 0) return "vence hoje";
      if (dias === 1) return "amanhã";

      return `em ${dias} dias`;
    },

    classeSaldo,

    formatData,
    formatDocumento,
    formatMoney,
  },
};
</script>

<style scoped src="../cashback.css"></style>

<style scoped>
.cashback-extrato__topo {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.cashback-extrato__acoes {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.cashback-exportar >>> .dropdown-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.cashback-exportar__dica {
  max-width: 280px;
  padding: 2px 20px 6px;
  font-size: 11.5px;
  color: #667085;
  white-space: normal;
}

.cashback-exportar__dica--bloqueio {
  color: #b42318;
}

.cashback-exportar__icone {
  width: 16px;
  margin-right: 8px;
  color: #667085;
}

.cashback-extrato__bloqueio {
  display: flex;
  align-items: flex-start;
  padding: 10px 14px;
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid #fecdca;
  background-color: #fef3f2;
  color: #912018;
}

.cashback-extrato__bloqueio-motivo {
  margin-top: 2px;
  color: #b42318;
  font-size: 13px;
}

.cashback-extrato__nome {
  font-size: 16px;
  font-weight: 600;
  color: #101828;
}

.cashback-extrato__detalhe {
  font-size: 12px;
  color: #667085;
}

.cashback-extrato__cards {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.cashback-extrato__card {
  flex: 1 1 200px;
  padding: 14px 16px;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  background-color: #fff;
}

.cashback-extrato__rotulo {
  margin-bottom: 4px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #667085;
}

.cashback-extrato__valor {
  font-size: 21px;
  font-weight: 600;
  color: #101828;
}

.cashback-extrato__valor small {
  font-size: 12px;
  font-weight: 400;
  color: #667085;
}

.cashback-extrato__valor.cashback-valor--positivo {
  color: #027a48;
}

.cashback-extrato__valor.cashback-valor--negativo {
  color: #b42318;
}

.cashback-extrato__valor--atencao {
  color: #b54708;
}

.cashback-extrato__filtros {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 210px 160px 160px max-content;
  gap: 8px;
  align-items: center;
}

.cashback-pendente-card {
  flex: 1 1 200px;
  padding: 14px 16px;
  border: 1px solid #fedf89;
  border-radius: 10px;
  background-color: #fffcf5;
}

.cashback-pendente-card__rotulo {
  margin-bottom: 4px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #b54708;
}

.cashback-pendente-card__valor {
  font-size: 21px;
  font-weight: 600;
  color: #b54708;
}

.cashback-pendente-card__valor small {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #667085;
}

.cashback-pendente-card__valor--vazio {
  color: #98a2b3;
}

.cashback-pendente__linha td {
  background-color: #fffbeb;
}

.cashback-pendente__etiqueta {
  background-color: #fef3c7;
  color: #b54708;
}

.cashback-pendente__valor {
  font-weight: 700;
  white-space: nowrap;
  color: #b54708;
}

.cashback-pendente__previsto {
  display: block;
  font-size: 11px;
  font-weight: 500;
  color: #b54708;
}

.cashback-vencimento__linha {
  cursor: pointer;
}

.cashback-vencimento__linha:focus-visible {
  outline: 2px solid rgba(var(--cashback-cor-primaria-rgb), 0.4);
  outline-offset: -2px;
}

.cashback-vencimento__linha:hover td {
  background-color: #fafbfc;
}

.cashback-vencimento__icone {
  margin-right: 6px;
  color: #b54708;
}

.cashback-vencimento__prazo {
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 10px;
  background-color: #fef3c7;
  font-size: 11px;
  font-weight: 600;
  color: #b54708;
}

.cashback-vencimento__valor {
  font-weight: 700;
  white-space: nowrap;
}

.cashback-vencimento__seta {
  text-align: right;
  color: #98a2b3;
}

.cashback-vencimento__detalhe td {
  padding-top: 6px;
  padding-bottom: 6px;
  background-color: #f9fafb;
  font-size: 12.5px;
  color: #475467;
}

@media (max-width: 992px) {
  .cashback-extrato__filtros {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
